import React, { FC, useEffect, useState } from "react";
import Link from "next/link";

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import { cn } from "@src/lib/utils/cn";
import LeftNavOrgSwitcher from "@src/features/app/left-nav/LeftNavOrgSwitcher";
import AdminLeftNavNewButton from "@src/features/app/left-nav/admin/AdminLeftNavNewButton";
import { useOperationsVisited } from "@src/features/app/left-nav/admin/operationsVisited";
import useNewMessagingEnabled from "@src/features/messaging/posts/useNewMessagingEnabled";
import useMessagingActivityUnreadCount from "@src/features/messaging/new/activity/useMessagingActivityUnreadCount";
import PlusIcon from "@src/lib/icons/18px/plus";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import useHasCustomerFacingAccountPlan from "@src/features/settings/accountPlan/useHasCustomerFacingAccountPlan";
import { useRouter } from "next/router";

import {
  AdminNavSectionId,
  AdminPrimaryNavItem,
  adminSettingsNavItem,
} from "./adminNavConfig";

type Props = {
  items: AdminPrimaryNavItem[];
  activeSectionId: AdminNavSectionId;
  onSelectSection: (sectionId: AdminNavSectionId) => void;
  // When true (e.g. on screens without a secondary menu), keep the rail
  // permanently expanded and skip the floating-overlay treatment so it
  // behaves as a real persistent column.
  forceExpanded?: boolean;
  hideNewButton?: boolean;
  hideSettings?: boolean;
};

const PRIMARY_RAIL_MENU_BUTTON_CLASS = cn(
  "relative isolate h-8 gap-4 px-1.5 text-s-v4 text-gray-11",
  "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1.5 group-data-[collapsible=icon]:!text-s-v4",
  "[&>svg]:!size-4 [&>svg]:translate-x-[2.5px]",
  "data-[active=true]:!bg-white data-[active=true]:!shadow-out-sm data-[active=true]:hover:!bg-white",
  // Labels: fade in 150ms when the rail expands; disappear instantly on
  // collapse (the 80ms width transition + overflow-hidden on the rail clip
  // anything still painting).
  "[&>span:last-child]:opacity-100 [&>span:last-child]:transition-opacity [&>span:last-child]:duration-150 [&>span:last-child]:ease-in-out [&>span:last-child]:motion-reduce:transition-none",
  "group-data-[collapsible=icon]:[&>span:last-child]:opacity-0 group-data-[collapsible=icon]:[&>span:last-child]:!transition-none",
);

const AdminPrimaryNavRail: FC<Props> = ({
  items,
  activeSectionId,
  onSelectSection,
  forceExpanded = false,
  hideNewButton = false,
  hideSettings = false,
}) => {
  const { primaryRailPinned } = useSidebar();
  const [hovered, setHovered] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);

  const expanded = forceExpanded || hovered || primaryRailPinned || newMenuOpen;
  // Floating overlay treatment (white card + shadow) only applies when the rail
  // is acting as a transient hover/pinned panel over a secondary menu.
  const floating = expanded && !forceExpanded;

  const openRail = () => {
    setHovered(true);
  };

  const closeRail = () => {
    if (newMenuOpen) return;
    setHovered(false);
  };

  const collapseTransientRail = () => {
    if (primaryRailPinned) return;
    setHovered(false);
  };

  const selectSection = (sectionId: AdminNavSectionId) => {
    onSelectSection(sectionId);
    collapseTransientRail();
  };

  return (
    <nav
      aria-label="Main"
      data-expanded={expanded}
      data-pinned={primaryRailPinned}
      data-collapsible={expanded ? "" : "icon"}
      className={cn(
        "group fixed inset-y-0 left-0 z-40 hidden w-[var(--admin-primary-rail-collapsed-width)] flex-col overflow-hidden bg-transparent transition-[width,background-color,box-shadow,border-radius] duration-[80ms] ease-[cubic-bezier(0.71,0,0.17,1)] motion-reduce:transition-none md:flex",
        expanded && "w-[var(--admin-primary-rail-expanded-width)]",
        floating &&
          "rounded-r-xl bg-[#f9f8f6] shadow-[0_10px_32px_rgba(15,23,42,0.10)]",
      )}
      onMouseEnter={openRail}
      onMouseLeave={closeRail}
      onFocus={openRail}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (newMenuOpen) return;
        closeRail();
      }}
    >
      <div className="flex h-14 items-center px-3">
        <LeftNavOrgSwitcher
          triggerClassName={cn(
            "flex h-8 gap-2.5 px-1 pr-2 text-s-v4",
            "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1",
          )}
        />
      </div>

      <div className="flex hide-scrollbar min-h-0 flex-1 flex-col items-start overflow-auto px-3 pt-1 pb-3">
        <SidebarMenu>
          {!hideNewButton && (
            <SidebarMenuItem>
              <AdminLeftNavNewButton
                onMenuOpenChange={setNewMenuOpen}
                trigger={
                  <SidebarMenuButton className={PRIMARY_RAIL_MENU_BUTTON_CLASS}>
                    <PlusIcon />
                    <span>New</span>
                  </SidebarMenuButton>
                }
              />
            </SidebarMenuItem>
          )}
          {items.map((item) => (
            <PrimaryRailMenuItem
              key={item.id}
              item={item}
              active={activeSectionId === item.id}
              expanded={expanded}
              onSelectSection={selectSection}
            />
          ))}
        </SidebarMenu>
      </div>

      {!hideSettings && (
        <div className="flex flex-col items-start gap-1 px-3 pt-2 pb-3">
          <SidebarMenu>
            <AccountPlanRailItem />
            <PrimaryRailMenuItem
              item={adminSettingsNavItem}
              active={activeSectionId === "settings"}
              expanded={expanded}
              onSelectSection={selectSection}
            />
          </SidebarMenu>
        </div>
      )}
    </nav>
  );
};

const AccountPlanRailItem: FC = () => {
  const { pathname } = useRouter();
  const hasPlan = useHasCustomerFacingAccountPlan();
  if (!hasPlan) return null;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={pathname === "/account-plan"}
        className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
        aria-current={pathname === "/account-plan" ? "page" : undefined}
      >
        <Link href="/account-plan">
          <FileTextOutlineIcon />
          <span>Account Plan</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const PrimaryRailMenuItem: FC<{
  item: AdminPrimaryNavItem;
  active: boolean;
  expanded: boolean;
  onSelectSection: (sectionId: AdminNavSectionId) => void;
}> = ({ item, active, expanded, onSelectSection }) => {
  const Icon = item.Icon;
  const { setOpen } = useSidebar();
  const [operationsVisited, markOperationsVisited] = useOperationsVisited();
  const hasSecondaryNav =
    item.id === "reporting" ||
    item.id === "recommendations" ||
    item.items.length > 0;

  // "New" indicator: a red dot on the top-right of the Operations icon,
  // shown until the user first opens Operations. Absolutely positioned and
  // left-anchored so it stays on the icon in both the collapsed and expanded
  // rail, and never affects the label layout.
  const showNewDot = item.id === "operations" && !operationsVisited;
  const newDot = showNewDot ? (
    <span
      data-testid="operations-nav-new-dot"
      className="pointer-events-none absolute top-[5px] left-[18px] size-2 rounded-full bg-red-8"
    />
  ) : null;

  const newMessagingEnabled = useNewMessagingEnabled();
  const showMessagesBadge = item.id === "messages" && newMessagingEnabled;
  const unreadCount = useMessagingActivityUnreadCount(!showMessagesBadge);
  const hasUnread = showMessagesBadge && unreadCount > 0;

  const unreadPill =
    hasUnread && expanded ? (
      <span className="ml-auto flex h-[15px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-none font-semibold tracking-[-0.5px] text-white">
        {unreadCount}
      </span>
    ) : null;
  const unreadDot =
    hasUnread && !expanded ? (
      <span className="pointer-events-none absolute top-[5px] left-[18px] size-2 rounded-full bg-brand" />
    ) : null;

  useEffect(() => {
    if (item.id === "operations" && active) markOperationsVisited();
  }, [item.id, active, markOperationsVisited]);

  const selectItem = () => {
    if (item.id === "operations") markOperationsVisited();
    if (hasSecondaryNav) setOpen(true);
    onSelectSection(item.id);
  };

  if (item.href) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active}
          className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
          aria-current={active ? "page" : undefined}
          onClick={selectItem}
        >
          <Link href={item.href as React.ComponentProps<typeof Link>["href"]}>
            <Icon />
            {newDot}
            {unreadDot}
            <span>{item.label}</span>
            {unreadPill}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
        aria-current={active ? "page" : undefined}
        onClick={selectItem}
      >
        <Icon />
        {newDot}
        {unreadDot}
        <span>{item.label}</span>
        {unreadPill}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export default AdminPrimaryNavRail;
